import {
  GIGA_BYTE_SIZE,
  LimitStorageUnit,
  TERA_BYTE_SIZE,
} from "@utils/limit-data-display";
import { FaroCoreApiTypes } from "@stellar/core-api-client";

/** Decimals kept when converting a storage value from bytes into GB/TB, e.g. 2.5 TB */
const STORAGE_VALUE_DECIMALS = 2;

/** Get the value of the given limit, converting storage from bytes to the given unit */
export function getValueOfLimit(
  limit: FaroCoreApiTypes.LimitPayload,
  storageUnit: LimitStorageUnit = "GB"
): number {
  if (limit.type !== "STORAGE") {
    return limit.value;
  }
  return Number(
    (limit.value / storageUnitToBytes(1, storageUnit)).toFixed(
      STORAGE_VALUE_DECIMALS
    )
  );
}

/** Convert a storage value expressed in the given unit into bytes */
export function storageUnitToBytes(
  value: number,
  storageUnit: LimitStorageUnit
): number {
  return value * (storageUnit === "TB" ? TERA_BYTE_SIZE : GIGA_BYTE_SIZE);
}

/**
 * A limit payload plus frontend-only configuration describing how the user may
 * edit it while creating a plan.
 */
export type ConfigurableLimitPayload = FaroCoreApiTypes.LimitPayload & {
  /** Whether the limit value is fixed and cannot be modified by the user */
  isFixed?: boolean;
};

type CreateLimitPayloadProps = {
  /**
   * Type of the limit. Both USER and ACTIVE_SEAT limit the active users: USER through the older
   * USER_ROLE constraint, ACTIVE_SEAT through the constraint the 2026 plans use instead
   */
  type: "USER" | "ACTIVE_SEAT" | "PROJECT" | "STORAGE";

  /** Limit value for the limit */
  limit: number;

  /** Storage unit for the limit value. Defaults to TB. Only applies to STORAGE */
  unit?: LimitStorageUnit;

  /** Whether the limit value is fixed and cannot be modified by the user */
  isFixed?: boolean;
};

/** Create limits payloads from limit inputs */
export function createLimitPayload(
  limits: CreateLimitPayloadProps[]
): ConfigurableLimitPayload[] {
  return limits.map((limit) => {
    const payload = toBaseLimitPayload(limit);
    return limit.isFixed ? { ...payload, isFixed: true } : payload;
  });
}

/** Build the API limit payload for a single limit input */
function toBaseLimitPayload({
  type,
  limit,
  unit,
}: CreateLimitPayloadProps): FaroCoreApiTypes.LimitPayload {
  switch (type) {
    /** Create user role active limits */
    case "USER":
      return {
        type: "USER_ROLE",
        value: limit,
        roles: allRolesForUserLimit,
      };

    /** Create active seat limits, which do not depend on the roles of the users */
    case "ACTIVE_SEAT":
      return {
        type: "ACTIVE_SEAT",
        value: limit,
      };

    /** Create project count active limits */
    case "PROJECT":
      return {
        type: "PROJECT_COUNT",
        value: limit,
      };

    /** Create storage limit active limits */
    case "STORAGE":
      return {
        type: "STORAGE",
        value: storageUnitToBytes(limit, unit ?? "TB"),
      };

    default:
      throw new Error(`Unsupported limit type: ${type}`);
  }
}

/** Whether the limit type limits the active users of a plan, through either of its constraints */
export function isActiveUsersLimitType(
  type: FaroCoreApiTypes.LimitType
): boolean {
  return type === "USER_ROLE" || type === "ACTIVE_SEAT";
}

/** The type of tier that can be selected for a plan This is used to define the storage and processing limits for each tier */
export type TierType =
  "tier1" | "tier2" | "tier3" | "tier4" | "tier5" | "tier6";

/** All available tiers with their labels and storage limits */
export const AllAvailableTiers: Record<
  TierType,
  { label: string; storageLimit: number }
> = {
  tier1: { label: "Tier 1", storageLimit: 7.5 },
  tier2: { label: "Tier 2", storageLimit: 10 },
  tier3: { label: "Tier 3", storageLimit: 15 },
  tier4: { label: "Tier 4", storageLimit: 25 },
  tier5: { label: "Tier 5", storageLimit: 40 },
  tier6: { label: "Tier 6", storageLimit: 60 },
};

/** All the roles that is needed for userRole limit */
export const allRolesForUserLimit: FaroCoreApiTypes.LimitUserRoles = {
  company: ["enterprise_admin", "group_manager", "project_manager"],
  resource: ["owner", "admin", "editor"],
};
